import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Star, Upload } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { key: "name", label: "Contact Name *", w: "min-w-[150px]" },
  { key: "title", label: "Designation", w: "min-w-[130px]" },
  { key: "department", label: "Department", w: "min-w-[120px]" },
  { key: "email", label: "Email", w: "min-w-[190px]" },
  { key: "phone", label: "Phone", w: "min-w-[130px]" },
  { key: "extension", label: "Ext", w: "w-16" },
  { key: "linkedin", label: "LinkedIn URL", w: "min-w-[150px]" },
  { key: "location", label: "Location", w: "min-w-[120px]" },
];

let _rid = 0;
const rid = () => `poc-${Date.now()}-${_rid++}`;

export const emptyPOC = () => ({ id: rid(), name: "", title: "", department: "", email: "", phone: "", extension: "", linkedin: "", location: "", is_primary: false, notes: "" });

export default function POCGrid({ pocs, setPocs, readOnly = false }) {
  const fileRef = useRef(null);

  const update = (id, key, val) => setPocs(pocs.map((p) => (p.id === id ? { ...p, [key]: val } : p)));
  const setPrimary = (id) => setPocs(pocs.map((p) => ({ ...p, is_primary: p.id === id })));
  const addRow = () => setPocs([...pocs, { ...emptyPOC(), is_primary: pocs.length === 0 }]);
  const delRow = (id) => {
    const next = pocs.filter((p) => p.id !== id);
    if (next.length && !next.some((p) => p.is_primary)) next[0].is_primary = true;
    setPocs(next);
  };

  const emails = pocs.map((p) => (p.email || "").trim().toLowerCase()).filter(Boolean);
  const dupEmail = (e) => e && emails.filter((x) => x === (e || "").trim().toLowerCase()).length > 1;

  // Paste multi-row from Excel/Google Sheets (tab-separated). Fired on the Name cell.
  const onPaste = (startId, e) => {
    const text = e.clipboardData.getData("text");
    if (!text || (!text.includes("\t") && !text.includes("\n"))) return; // single value → default paste
    e.preventDefault();
    const rows = text.split(/\r?\n/).filter((r) => r.trim());
    const parsed = rows.map((r) => {
      const c = r.split("\t");
      return { ...emptyPOC(), name: c[0] || "", title: c[1] || "", department: c[2] || "", email: c[3] || "", phone: c[4] || "", extension: c[5] || "", linkedin: c[6] || "", location: c[7] || "" };
    });
    const idx = pocs.findIndex((p) => p.id === startId);
    const next = [...pocs];
    next.splice(idx, 1, ...parsed); // replace the row being pasted into
    if (!next.some((p) => p.is_primary) && next.length) next[0].is_primary = true;
    setPocs(next);
    toast.success(`Pasted ${parsed.length} POC row(s)`);
  };

  const onCSV = async (file) => {
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return;
    const start = /name/i.test(lines[0]) ? 1 : 0; // skip header row
    const parsed = lines.slice(start).map((l) => {
      const c = l.split(",").map((x) => x.trim().replace(/^"|"$/g, ""));
      return { ...emptyPOC(), name: c[0] || "", title: c[1] || "", department: c[2] || "", email: c[3] || "", phone: c[4] || "", extension: c[5] || "", linkedin: c[6] || "", location: c[7] || "" };
    });
    const next = [...pocs.filter((p) => p.name || p.email), ...parsed];
    if (next.length && !next.some((p) => p.is_primary)) next[0].is_primary = true;
    setPocs(next);
    toast.success(`Imported ${parsed.length} POC row(s)`);
  };

  return (
    <div data-testid="poc-grid">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium">Points of Contact <span className="text-muted-foreground font-normal">({pocs.length})</span></div>
        {!readOnly && (
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".csv" hidden onChange={(e) => { onCSV(e.target.files?.[0]); e.target.value = ""; }} />
            <Button type="button" size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => fileRef.current?.click()} data-testid="poc-import-csv"><Upload className="h-3.5 w-3.5" /> Import CSV</Button>
            <Button type="button" size="sm" variant="outline" className="gap-1.5 h-8" onClick={addRow} data-testid="poc-add-row"><Plus className="h-3.5 w-3.5" /> Add POC</Button>
          </div>
        )}
      </div>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/60 sticky top-0">
            <tr>
              <th className="w-10 p-2 text-xs font-medium text-muted-foreground text-center">#</th>
              <th className="w-14 p-2 text-xs font-medium text-muted-foreground text-center" title="Primary POC">Primary</th>
              {COLS.map((c) => <th key={c.key} className={`p-2 text-left text-xs font-medium text-muted-foreground ${c.w}`}>{c.label}</th>)}
              {!readOnly && <th className="w-10 p-2"></th>}
            </tr>
          </thead>
          <tbody>
            {pocs.length === 0 && (
              <tr><td colSpan={COLS.length + 3} className="p-4 text-center text-sm text-muted-foreground">No POCs yet. Add a row, paste from a spreadsheet, or import a CSV.</td></tr>
            )}
            {pocs.map((p, i) => (
              <tr key={p.id} className="border-t border-border" data-testid={`poc-row-${i}`}>
                <td className="p-2 text-center text-xs text-muted-foreground bg-muted/30">{i + 1}</td>
                <td className="p-2 text-center">
                  <button type="button" disabled={readOnly} onClick={() => setPrimary(p.id)} data-testid={`poc-primary-${i}`} title="Set primary">
                    <Star className={`h-4 w-4 ${p.is_primary ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                  </button>
                </td>
                {COLS.map((c) => (
                  <td key={c.key} className="p-1">
                    <Input
                      value={p[c.key] || ""}
                      readOnly={readOnly}
                      onChange={(e) => update(p.id, c.key, e.target.value)}
                      onPaste={c.key === "name" ? (e) => onPaste(p.id, e) : undefined}
                      data-testid={`poc-${i}-${c.key}`}
                      className={`h-8 border-0 focus-visible:ring-1 ${c.key === "email" && dupEmail(p.email) ? "bg-red-50 text-red-700" : ""}`}
                    />
                  </td>
                ))}
                {!readOnly && (
                  <td className="p-1 text-center">
                    <button type="button" onClick={() => delRow(p.id)} data-testid={`poc-delete-${i}`} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1.5">Tip: paste multiple rows straight from Excel/Google Sheets into the Contact Name cell. One Primary POC is required.</p>
    </div>
  );
}
